import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ProjectSite, loadProjectSites } from '../cbos/projectSites';
import { useSelectedProject } from '../cbos/selectedProject';

/**
 * Which property of the project the dashboard is looking at.
 *
 * The project itself is chosen in the sidebar, for the whole of CBOS — it governs every screen
 * there, so it belongs to none of them in particular. What is left here is the choice this screen
 * really owns: which of that project's properties.
 *
 * The dashboard used to offer any address the browser had seen, which let it show a property that
 * belonged to no project — and a perspective written against such an address had nothing to hang
 * on. A site is reachable only through the project that owns it, so what is on screen is always
 * part of the portfolio being worked on.
 */

interface Properties {
    selectedProjectId: string | null;
    selectedSiteId: string | null;
    onSelect: (projectId: string | null, siteId: string | null) => void;
    /** Reports the picked site so the page can resolve its building. */
    onSiteChange: (site: ProjectSite | null) => void;
}

/**
 * An empty string means "no selection" to the select primitive, so a record whose id resolves to
 * nothing would take the placeholder's place and throw. It travels under a sentinel instead and is
 * handed back as the empty string it was.
 */
const EMPTY_KEY = '__empty__';
const toValue = (key: string | null): string | undefined =>
    key === null ? undefined : key === '' ? EMPTY_KEY : key;
const fromValue = (value: string): string => (value === EMPTY_KEY ? '' : value);

export default function ObjectSelectionBar({
    selectedProjectId,
    selectedSiteId,
    onSelect,
    onSiteChange,
}: Properties) {
    const { t } = useTranslation();
    const siteFieldId = useId();
    // The same list the sidebar's picker reads, cached per tenant: the project object is needed
    // here only to reach its sites.
    const { project } = useSelectedProject();
    const [sites, setSites] = useState<ProjectSite[]>([]);
    const [loadingSites, setLoadingSites] = useState(false);

    useEffect(() => {
        if (!project) {
            setSites([]);
            setLoadingSites(false);
            return;
        }

        let cancelled = false;
        setLoadingSites(true);

        loadProjectSites(project)
            .then((found) => {
                if (cancelled) return;
                setSites(found);

                // A project with exactly one site has nothing to choose; picking it saves a click
                // and means the page is never left half-selected.
                if (found.length === 1 && !selectedSiteId) {
                    onSelect(selectedProjectId, found[0].id);
                }
            })
            .catch((error: any) => console.error('Could not load the project sites', error))
            .finally(() => {
                if (!cancelled) setLoadingSites(false);
            });

        return () => {
            cancelled = true;
        };
        // `onSelect` is a fresh closure on every render of the page.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project, selectedProjectId]);

    // Whatever the site id points at is what the page resolves a building from.
    useEffect(() => {
        onSiteChange(sites.find((site) => site.id === selectedSiteId) ?? null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sites, selectedSiteId]);

    return (
        <div className="flex min-w-0 flex-1 flex-wrap items-end gap-2">
            <div className="flex min-w-0 flex-col gap-1.5">
                <Label htmlFor={siteFieldId}>{t('portfolioDashboard.objectSelection.site')}</Label>
                <Select
                    value={toValue(selectedSiteId)}
                    disabled={!selectedProjectId || loadingSites}
                    onValueChange={(value) => onSelect(selectedProjectId, fromValue(value))}
                >
                    <SelectTrigger id={siteFieldId} className="w-full sm:w-[320px]">
                        <SelectValue
                            placeholder={
                                selectedProjectId
                                    ? t('portfolioDashboard.objectSelection.selectSite')
                                    : t('portfolioDashboard.objectSelection.selectProjectFirst')
                            }
                        />
                    </SelectTrigger>
                    <SelectContent>
                        {sites.map((site) => (
                            <SelectItem key={site.id} value={toValue(site.id) ?? EMPTY_KEY}>
                                {site.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {loadingSites ? (
                <Loader2
                    className="mb-2 h-4 w-4 shrink-0 animate-spin text-muted-foreground"
                    role="status"
                    aria-label={t('global.loading')}
                />
            ) : (
                <></>
            )}

            {selectedProjectId && !loadingSites && sites.length === 0 ? (
                <span className="mb-2 text-sm text-muted-foreground">
                    {t('portfolioDashboard.objectSelection.noSites')}
                </span>
            ) : (
                <></>
            )}
        </div>
    );
}
